import { useState, useEffect } from "react";
import Swipeable from "@/components/Swipeable";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Home,
  MessageSquare,
  Copy,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
  Send,
  User,
  FileText,
  Heart,
  Bell,
  Calendar,
  Star,
  Sparkles,
  ExternalLink,
  Menu,
  Wand2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";
import { isAdvisor, authGet, authPost, authPut, authDelete } from "../utils/auth";

// Message Categories
const MESSAGE_CATEGORIES = [
  { id: "lead", name: "New Lead", icon: User, color: "from-[#EDF1F8] to-indigo-600" },
  { id: "followup", name: "Follow-up", icon: Bell, color: "from-[#D4A017] to-[#D9A82C]" },
  { id: "documents", name: "Documents", icon: FileText, color: "from-emerald-500 to-green-600" },
  { id: "policy", name: "Policy Issued", icon: Heart, color: "from-pink-500 to-rose-600" },
  { id: "relationship", name: "Relationship", icon: Calendar, color: "from-purple-500 to-violet-600" },
  { id: "special", name: "Special Occasions", icon: Star, color: "from-cyan-500 to-[#DCE4F2]" },
];

// Message Templates
const MESSAGE_TEMPLATES = [
  {
    id: "intro-1",
    category: "lead",
    title: "Introduction Message",
    description: "First message to new leads from Beshak",
    message: `Hello (Client Name)

It was a pleasure connecting with you via Beshak.

I'm Prashant K Uunadkat, a Beshak Verified Advisor with over 8 years of experience in the insurance industry. My role is to simplify insurance decisions and help you choose the right protection with clarity and confidence—whether it's Health Insurance, Term Insurance, or any other coverage.

My advisory approach is claim-first, unbiased, and client-centric, with a clear focus on ensuring that policies work smoothly at the time of claim.

To know more about my background, advisory philosophy, and the way I work, you may also visit my website:
www.prashantkuunadkat.com

I look forward to connecting with you, understanding your requirements, and guiding you toward the most suitable options.

Warm regards,
Prashant K Uunadkat
Beshak Verified Advisor`,
    placeholders: ["(Client Name)"],
    tags: ["introduction", "beshak", "first contact"]
  },
  {
    id: "followup-truematch",
    category: "followup",
    title: "TrueMatch Report Reminder",
    description: "Ask client to generate TrueMatch report before meeting",
    message: `If you haven't generated your TrueMatch Report, Please generate it before the meeting. Here's the link

If you are looking for Health Insurance
https://www.beshak.org/insurance/health-insurance/trumatch/app/

If you are looking for Term Insurance
https://www.beshak.org/insurance/term-life-insurance/trumatch/`,
    placeholders: [],
    tags: ["truematch", "beshak", "pre-meeting", "health", "term"]
  },
  {
    id: "followup-quotation",
    category: "followup",
    title: "Quotation Shared",
    description: "Message when sharing quotation PDF with client",
    message: `Hi (Client Name),

Based on your requirements, I've analyzed your details and prepared the quotation PDF for you — please have a look when convenient.

Just a quick note: premiums can vary across platforms due to factors like age, pin code, selected riders, coverage structure, and available discounts. So if you happen to see a different quote elsewhere, feel free to reach out — I'll help you understand exactly why the premium differs (so you're comparing apples to apples).

Let me know a convenient time for a quick call, and we can walk through the quotation together and clarify any doubts.

Looking forward.`,
    placeholders: ["(Client Name)"],
    tags: ["quotation", "premium", "comparison", "call"]
  },
  {
    id: "followup-quotation-24hr",
    category: "followup",
    title: "Quotation Follow-up (24 hrs)",
    description: "Gentle reminder if client hasn't responded to quotation",
    message: `Hi (Client Name),

Just checking in — were you able to review the quotation I shared?

If any doubts or if you'd like me to walk you through the options, feel free to suggest a time for a quick call.

Happy to help!`,
    placeholders: ["(Client Name)"],
    tags: ["quotation", "follow-up", "reminder", "24-hours"]
  },
  {
    id: "followup-lead-closing",
    category: "followup",
    title: "Lead Closing (No Response)",
    description: "Professional closure when client doesn't respond",
    message: `Hi (Client Name),

At Beshak, we follow a no-spam policy — so since I haven't heard back from you, I'll be closing this enquiry for now.

That said, if you ever wish to revisit your insurance planning, feel free to reach out anytime. I'll be happy to assist.

Wishing you good health and financial security!

Warm regards,
Prashant K Uunadkat`,
    placeholders: ["(Client Name)"],
    tags: ["closing", "no-response", "no-spam", "beshak"]
  },
  {
    id: "followup-post-discussion",
    category: "followup",
    title: "Post Quote Discussion Follow-up",
    description: "Follow-up after quote discussion meeting",
    message: `Hi (Client Name),

It was great discussing the quotation with you!

Just wanted to check in — have you had a chance to finalize your decision?

If you have any more questions or need further clarity on any aspect, feel free to ask. I'm happy to help you make an informed choice.

Once you're ready to proceed, just let me know and I'll send you the proposal form to move ahead.

Looking forward to hearing from you!`,
    placeholders: ["(Client Name)"],
    tags: ["quote-discussion", "decision", "proposal", "follow-up"]
  },
  {
    id: "documents-health-insurance",
    category: "documents",
    title: "Health Insurance - Document Request",
    description: "Complete document checklist for health insurance application",
    message: `Dear (Client Name),

To proceed smoothly with your health insurance application, please keep the following documents/details ready:
--------------------------
1. For a Fresh Policy

• Proposer's Documents
• Aadhaar Card
• PAN Card
• Cancelled Cheque (for refund/NEFT purposes)

Nominee Details

• Full Name
• Date of Birth
• Relationship with Proposer

Insured Member Documents & Details

• Aadhaar Card of all insured members
• Height & Weight of each insured member
• Medical History (if any) – e.g., diabetes, hypertension, thyroid, etc.
• Past Hospitalization Details – year, reason, treatment taken
• Current Medication Details – ongoing medicines or treatments

Contact Details

• Active Email ID
• Mobile Number (to be registered with the policy)

2. For a Portability Case (shifting from an existing insurer to a new one)

Along with the above, you will also need:

• Previous Policy Copy of 3 years (all years of renewal if available)

You can send all these details on 
Community@beshak.org 
And CC: prasant@shreefinservices.com`,
    placeholders: ["(Client Name)"],
    tags: ["documents", "health-insurance", "fresh-policy", "portability", "checklist"]
  },
  {
    id: "relationship-advisor-importance",
    category: "relationship",
    title: "Importance of Advisor & Beshak Cares",
    description: "Explain why having an advisor matters at claim time",
    message: `Hi (Client Name),

I wanted to share something important about why having the right advisor matters — especially at the time of a claim.

𝗪𝗵𝘆 𝗮𝗻 𝗔𝗱𝘃𝗶𝘀𝗼𝗿 𝗠𝗮𝘁𝘁𝗲𝗿𝘀 𝗮𝘁 𝗖𝗹𝗮𝗶𝗺 𝗧𝗶𝗺𝗲:

• We understand how hospitals bill and what insurers look for — so we can guide you to avoid unnecessary claim issues.
• If a valid claim gets wrongly rejected, we know how to fight back — from escalations to the Ombudsman, and even courts if needed.
• Unlike call centre agents, we have real skin in the game — your claim experience is our reputation.

𝗪𝗵𝘆 𝗕𝗲𝘀𝗵𝗮𝗸?

At Beshak, you're not dealing with salaried employees reading scripts. You're connected with real, independent insurance experts — professionals who run their own advisory practice and have years of hands-on experience in the industry.

𝗕𝗲𝘀𝗵𝗮𝗸 𝗖𝗔𝗥𝗘𝗦 – 𝗬𝗼𝘂𝗿 𝗕𝗮𝗰𝗸𝘂𝗽 𝗦𝘂𝗽𝗽𝗼𝗿𝘁

For policies taken through Beshak, you also get access to Beshak CARES — a Claims Assistance & Resolution Ecosystem that includes:

✅ 24/7 Helpdesk support
✅ Expert dispute resolution guidance
✅ Lifelong promise — if your advisor is ever unavailable, the Beshak community steps in

Learn more: https://cares.beshak.org/

With Beshak, you're never alone — before, during, or after a claim.`,
    placeholders: ["(Client Name)"],
    tags: ["advisor", "claims", "beshak-cares", "trust", "value"]
  },
  {
    id: "policy-issued-welcome",
    category: "policy",
    title: "Policy Issued - Welcome Message",
    description: "Congratulations message when policy is successfully issued",
    message: `Hello (Client Name),

Congratulations! Your policy has been successfully issued.

For any claims or policy service, I'll be your primary point of contact. However, in case I'm not reachable, you may also contact:

– CA Savan Pandat: +91-9909641196
– CA Kaushik Kakkad: +91-7984092849

And as a final layer of support, you can also raise your request directly through Beshak Cares:
👉 Register here: https://cares.beshak.org/

We're here to ensure you have full support—especially when it matters the most.`,
    placeholders: ["(Client Name)"],
    tags: ["policy-issued", "congratulations", "welcome", "support", "beshak-cares"]
  },
  {
    id: "relationship-quarterly-simple",
    category: "relationship",
    title: "Quarterly Check-in - Simple",
    description: "Casual informal message to stay in touch",
    message: `Hi (Client Name),

How are you? Hope you and your family are doing well!

Take care 🙂`,
    placeholders: ["(Client Name)"],
    tags: ["quarterly", "check-in", "informal", "relationship"]
  },
  {
    id: "relationship-quarterly-health",
    category: "relationship",
    title: "Quarterly - Health Checkup Reminder",
    description: "Reminder about annual health checkup benefit (use only if policy has this benefit)",
    message: `Hi (Client Name),

Quick health reminder 💡

If your policy includes the Annual Health Check-up benefit — now might be a good time to use it! Regular check-ups can help detect issues early.

Also, if there are any updates in your health or family situation (new member, address change, etc.), do let me know so we can keep your policy records current.

Stay healthy, stay covered!`,
    placeholders: ["(Client Name)"],
    tags: ["quarterly", "health-checkup", "reminder", "benefit"]
  },
  {
    id: "relationship-quarterly-referral",
    category: "relationship",
    title: "Quarterly - Referral Ask",
    description: "Soft touch message with referral request",
    message: `Hi (Client Name),

Hope all is well at your end!

If you know anyone in your family or friend circle who's looking for genuine insurance advice — feel free to connect them with me. I'd be happy to help them the same way I've helped you.

And as always, if you ever need any assistance with your policy, I'm just a message away.

Wishing you a healthy and secure life ahead!`,
    placeholders: ["(Client Name)"],
    tags: ["quarterly", "referral", "relationship", "soft-touch"]
  },
  {
    id: "relationship-cross-sell",
    category: "relationship",
    title: "Cross-Sell - Wealth Management Services",
    description: "Introduce other financial services offered by Shree Finserv",
    message: `Hi (Client Name),

Hope you're doing well!

I wanted to share that apart from insurance advisory, we at Shree Finserv (Associated Partner with Beshak) also offer comprehensive wealth management and financial planning services.

Our services include:

📊 Portfolio Management
📈 Financial Planning
🏦 Tax Planning
📋 Insurance Planning
💼 Investments in Bonds, Unlisted Securities & Market Linked Debentures

Our team of Chartered Accountants brings deep expertise in managing and growing wealth — with a focus on your long-term financial goals.

If you or anyone in your family is looking for guidance on investments, tax-saving, or overall financial planning — feel free to reach out. We'd be happy to help.

Warm regards,
Prashant K Uunadkat
Shree Finserv | Beshak Verified Advisor`,
    placeholders: ["(Client Name)"],
    tags: ["cross-sell", "wealth-management", "financial-planning", "shree-finserv"]
  },
  {
    id: "special-birthday",
    category: "special",
    title: "Birthday Wishes",
    description: "Birthday greeting for clients",
    message: `Hi (Client Name),

Wishing you a very Happy Birthday! 🎂

May this year bring you good health, happiness, and success in everything you do.

Have a wonderful day!

Warm wishes,
Prashant`,
    placeholders: ["(Client Name)"],
    tags: ["birthday", "wishes", "celebration"]
  },
  {
    id: "special-diwali",
    category: "special",
    title: "Diwali Wishes",
    description: "Diwali festival greeting",
    message: `Hi (Client Name),

Wishing you and your family a very Happy Diwali! 🪔✨

May this festival of lights bring joy, prosperity, and good health to your home.

Have a safe and wonderful celebration!

Warm regards,
Prashant & Team Shree Finserv`,
    placeholders: ["(Client Name)"],
    tags: ["diwali", "festival", "wishes"]
  },
  {
    id: "special-newyear",
    category: "special",
    title: "New Year Wishes",
    description: "New Year greeting",
    message: `Hi (Client Name),

Happy New Year! 🎉

Wishing you and your family a year filled with good health, happiness, and prosperity.

Here's to new beginnings and achieving all your goals in the coming year!

Best wishes,
Prashant`,
    placeholders: ["(Client Name)"],
    tags: ["new-year", "wishes", "celebration"]
  },
  {
    id: "special-holi",
    category: "special",
    title: "Holi Wishes",
    description: "Holi festival greeting",
    message: `Hi (Client Name),

Wishing you a very Happy Holi! 🎨

May your life be as colorful and joyful as this beautiful festival.

Have a safe and happy celebration!

Warm wishes,
Prashant`,
    placeholders: ["(Client Name)"],
    tags: ["holi", "festival", "wishes"]
  },
  {
    id: "special-rakhi",
    category: "special",
    title: "Raksha Bandhan Wishes",
    description: "Raksha Bandhan / Rakhi greeting",
    message: `Hi (Client Name),

Wishing you a Happy Raksha Bandhan! 🪢

May the bond of love and protection grow stronger with each passing year.

Have a wonderful celebration with your loved ones!

Warm wishes,
Prashant`,
    placeholders: ["(Client Name)"],
    tags: ["raksha-bandhan", "rakhi", "festival", "wishes"]
  },
  // More templates will be added here
];

export default function ClientCommunication() {
  const navigate = useNavigate();
  const canManage = isAdvisor();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedCategories, setExpandedCategories] = useState({ lead: true, followup: true, documents: true, relationship: true, policy: true, special: true });
  const [copiedId, setCopiedId] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // Custom (AI-generated) templates stored in DB
  const [customTemplates, setCustomTemplates] = useState([]);

  // Generate / create modal
  const [showGenModal, setShowGenModal] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [genTone, setGenTone] = useState("warm and professional");
  const [genCategory, setGenCategory] = useState("followup");
  const [genTitle, setGenTitle] = useState("");
  const [genDescription, setGenDescription] = useState("");
  const [genMessage, setGenMessage] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit / delete modals
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchCustomTemplates = async () => {
    try {
      const res = await authGet("/api/advisor/message-templates");
      if (res.ok) {
        setCustomTemplates(res.data.templates || []);
      }
    } catch (err) {
      // silent - custom templates are additive
    }
  };

  useEffect(() => {
    fetchCustomTemplates();
  }, []);

  // Combined list: built-in + custom
  const allTemplates = [...customTemplates, ...MESSAGE_TEMPLATES];

  const resetGenModal = () => {
    setGenPrompt("");
    setGenTone("warm and professional");
    setGenCategory("followup");
    setGenTitle("");
    setGenDescription("");
    setGenMessage("");
    setGenerating(false);
    setSaving(false);
  };

  const openGenModal = () => {
    resetGenModal();
    setShowGenModal(true);
  };

  const handleGenerate = async () => {
    if (!genPrompt.trim()) {
      toast.error("Please describe the message you want");
      return;
    }
    setGenerating(true);
    try {
      const res = await authPost("/api/advisor/message-templates/generate", {
        prompt: genPrompt,
        tone: genTone,
        category: genCategory,
      });
      if (res.ok) {
        setGenMessage(res.data.message || "");
        if (!genTitle) setGenTitle(res.data.title || "");
        toast.success("Message generated! Review and save it.");
      } else {
        toast.error(res.data?.detail || "Failed to generate message");
      }
    } catch (err) {
      toast.error("Network error while generating");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveNew = async () => {
    if (!genTitle.trim()) {
      toast.error("Please add a title for this template");
      return;
    }
    if (!genMessage.trim()) {
      toast.error("The message cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const res = await authPost("/api/advisor/message-templates", {
        title: genTitle,
        description: genDescription,
        category: genCategory,
        message: genMessage,
        ai_prompt: genPrompt,
      });
      if (res.ok) {
        setCustomTemplates((prev) => [res.data.template, ...prev]);
        toast.success("Template saved!");
        setShowGenModal(false);
        resetGenModal();
      } else {
        toast.error(res.data?.detail || "Failed to save template");
      }
    } catch (err) {
      toast.error("Network error while saving");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editTemplate?.title?.trim()) {
      toast.error("Title cannot be empty");
      return;
    }
    if (!editTemplate?.message?.trim()) {
      toast.error("Message cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const res = await authPut(`/api/advisor/message-templates/${editTemplate.id}`, {
        title: editTemplate.title,
        description: editTemplate.description || "",
        category: editTemplate.category,
        message: editTemplate.message,
      });
      if (res.ok) {
        setCustomTemplates((prev) => prev.map((t) => (t.id === editTemplate.id ? res.data.template : t)));
        toast.success("Template updated!");
        setEditTemplate(null);
      } else {
        toast.error(res.data?.detail || "Failed to update template");
      }
    } catch (err) {
      toast.error("Network error while updating");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const res = await authDelete(`/api/advisor/message-templates/${deleteTarget.id}`);
      if (res.ok) {
        setCustomTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
        toast.success("Template deleted");
        setDeleteTarget(null);
      } else {
        toast.error(res.data?.detail || "Failed to delete template");
      }
    } catch (err) {
      toast.error("Network error while deleting");
    } finally {
      setSaving(false);
    }
  };

  // Filter messages
  const filteredMessages = allTemplates.filter(msg => {
    const matchesSearch = searchQuery === "" || 
      msg.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (msg.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === "all" || msg.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group messages by category
  const messagesByCategory = MESSAGE_CATEGORIES.map(cat => ({
    ...cat,
    messages: filteredMessages.filter(msg => msg.category === cat.id)
  })).filter(cat => cat.messages.length > 0);

  // Copy message to clipboard
  const handleCopy = async (message, id) => {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedId(id);
      toast.success("Message copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error("Failed to copy message");
    }
  };

  // Open WhatsApp with message
  const handleWhatsApp = (message) => {
    const encoded = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encoded}`, "_blank");
  };

  // Toggle category expansion
  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  // Highlight placeholders in message
  const highlightPlaceholders = (text, placeholders) => {
    let result = text;
    placeholders.forEach(placeholder => {
      result = result.replace(
        new RegExp(placeholder.replace(/[()]/g, '\\$&'), 'g'),
        `<span class="bg-[#D4A017]/30 text-[#A87608] px-1 rounded">${placeholder}</span>`
      );
    });
    return result;
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Messages</h1>
              <p className="text-xs text-[#3A4557]">{allTemplates.length} templates</p>
            </div>
          </div>
          {canManage && (
            <button
              onClick={openGenModal}
              data-testid="mobile-create-ai-message-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] text-[#0a1628] text-sm font-semibold active:scale-95 transition-transform"
            >
              <Wand2 className="h-4 w-4" />
              AI
            </button>
          )}
        </div>
        
        {/* Mobile Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm"
            />
          </div>
        </div>
        
        {/* Mobile Category Pills */}
        <Swipeable outerClassName="px-4 pb-3" className="flex gap-2">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              selectedCategory === "all"
                ? "bg-emerald-500 text-white"
                : "bg-[#EDF1F8]/50 text-[#3A4557]"
            }`}
          >
            All
          </button>
          {MESSAGE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.id
                  ? "bg-emerald-500 text-white"
                  : "bg-[#EDF1F8]/50 text-[#3A4557]"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </Swipeable>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/advisor/dashboard")}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <Home className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-[#1B2A4A]" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Client Communication</h1>
                <p className="text-xs text-[#3A4557]">Ready-made message templates</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {canManage && (
              <Button
                onClick={openGenModal}
                data-testid="create-ai-message-btn"
                className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C] hover:from-[#D9A82C] hover:to-[#D4A017] text-[#0a1628] font-semibold"
              >
                <Wand2 className="h-4 w-4 mr-2" />
                Generate with AI
              </Button>
            )}
            <div className="flex items-center gap-2 text-sm text-[#3A4557]">
              <Sparkles className="h-4 w-4 text-[#A87608]" />
              <span>{allTemplates.length} templates</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {/* Search and Filter - Desktop Only */}
        <div className="hidden md:flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>
          
          <Swipeable className="flex gap-2 pb-2 sm:pb-0">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === "all"
                  ? "bg-emerald-500 text-white"
                  : "bg-[#EDF1F8]/50 text-[#3A4557] hover:bg-[#E2E9F4]"
              }`}
            >
              All
            </button>
            {MESSAGE_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat.id
                    ? "bg-emerald-500 text-white"
                    : "bg-[#EDF1F8]/50 text-[#3A4557] hover:bg-[#E2E9F4]"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </Swipeable>
        </div>

        {/* Messages by Category */}
        {messagesByCategory.length === 0 ? (
          <div className="text-center py-16 bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl">
            <MessageSquare className="h-16 w-16 text-[#3A4557] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#3A4557] mb-2">No Messages Found</h3>
            <p className="text-[#3A4557]">Try adjusting your search or filter</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messagesByCategory.map(category => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#E2E9F4]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${category.color} flex items-center justify-center`}>
                      <category.icon className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-[#1B2A4A]">{category.name}</h3>
                      <p className="text-xs text-[#3A4557]">{category.messages.length} message{category.messages.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {expandedCategories[category.id] ? (
                    <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[#3A4557]" />
                  )}
                </button>

                {/* Messages List */}
                <AnimatePresence>
                  {expandedCategories[category.id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-[#C9D4E6]/50"
                    >
                      <div className="p-4 space-y-4">
                        {category.messages.map(msg => (
                          <div
                            key={msg.id}
                            className="bg-white/50 rounded-xl border border-[#C9D4E6]/30 overflow-hidden"
                          >
                            {/* Message Header */}
                            <div className="flex items-center justify-between p-4 border-b border-[#C9D4E6]/30">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-[#1B2A4A]">{msg.title}</h4>
                                  {msg.is_custom && (
                                    <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-[#D4A017]/20 text-[#A87608] rounded-full">
                                      <Wand2 className="h-2.5 w-2.5" />
                                      AI
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-[#3A4557] mt-0.5">{msg.description}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {msg.is_custom && canManage && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditTemplate({ ...msg })}
                                      data-testid={`edit-template-${msg.id}`}
                                      className="border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8] px-2"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setDeleteTarget(msg)}
                                      data-testid={`delete-template-${msg.id}`}
                                      className="border-red-500/40 text-red-600 hover:bg-red-500/10 px-2"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => handleWhatsApp(msg.message)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  WhatsApp
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleCopy(msg.message, msg.id)}
                                  variant="outline"
                                  className={`border-[#C9D4E6] ${
                                    copiedId === msg.id 
                                      ? "bg-emerald-500/20 border-emerald-500 text-emerald-600" 
                                      : "text-[#2E3B57] hover:bg-[#EDF1F8]"
                                  }`}
                                >
                                  {copiedId === msg.id ? (
                                    <>
                                      <Check className="h-4 w-4 mr-1" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-4 w-4 mr-1" />
                                      Copy
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Message Content */}
                            <div className="p-4">
                              {msg.placeholders && msg.placeholders.length > 0 && (
                                <div className="mb-3 flex items-center gap-2 text-xs text-[#A87608]">
                                  <Sparkles className="h-3 w-3" />
                                  <span>Replace highlighted text: {msg.placeholders.join(", ")}</span>
                                </div>
                              )}
                              <div 
                                className="text-[#2E3B57] text-sm whitespace-pre-wrap font-mono bg-white/70 p-4 rounded-lg border border-[#C9D4E6]/50 max-h-80 overflow-y-auto"
                                dangerouslySetInnerHTML={{ 
                                  __html: highlightPlaceholders(msg.message, msg.placeholders || [])
                                }}
                              />
                            </div>

                            {/* Tags */}
                            {msg.tags && msg.tags.length > 0 && (
                              <div className="px-4 pb-4 flex flex-wrap gap-2">
                                {msg.tags.map(tag => (
                                  <span 
                                    key={tag}
                                    className="px-2 py-1 text-xs bg-[#EDF1F8] text-[#3A4557] rounded-full"
                                  >
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-8 p-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-6 w-6 text-[#1B2A4A]" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-600 mb-1">Quick Tips</h3>
              <ul className="text-sm text-[#3A4557] space-y-1">
                <li>• Click <strong>Copy</strong> to copy the message to clipboard, then paste in WhatsApp</li>
                <li>• Click <strong>WhatsApp</strong> to directly open WhatsApp with the message</li>
                <li>• Replace <span className="bg-[#D4A017]/30 text-[#A87608] px-1 rounded">(Client Name)</span> with the actual client's name</li>
                <li>• More templates will be added as you share them</li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* Generate with AI Modal */}
      <Dialog open={showGenModal} onOpenChange={(o) => { if (!o) setShowGenModal(false); }}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="generate-message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1B2A4A]">
              <Wand2 className="h-5 w-5 text-[#A87608]" />
              Generate a Client Message
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              Describe what you want to say. AI will draft a ready-to-send WhatsApp message you can save as a template.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">What should the message say?</label>
              <Textarea
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                data-testid="ai-prompt-input"
                placeholder="e.g. A polite reminder to a client who hasn't submitted their KYC documents for their health policy"
                className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A] placeholder-[#9AA3B2] min-h-[90px]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Category</label>
                <Select value={genCategory} onValueChange={setGenCategory}>
                  <SelectTrigger className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A]" data-testid="ai-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6] text-[#1B2A4A]">
                    {MESSAGE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="focus:bg-[#EDF1F8]">{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Tone</label>
                <Select value={genTone} onValueChange={setGenTone}>
                  <SelectTrigger className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A]" data-testid="ai-tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#C9D4E6] text-[#1B2A4A]">
                    <SelectItem value="warm and professional" className="focus:bg-[#EDF1F8]">Warm & Professional</SelectItem>
                    <SelectItem value="friendly and casual" className="focus:bg-[#EDF1F8]">Friendly & Casual</SelectItem>
                    <SelectItem value="formal and concise" className="focus:bg-[#EDF1F8]">Formal & Concise</SelectItem>
                    <SelectItem value="empathetic and reassuring" className="focus:bg-[#EDF1F8]">Empathetic & Reassuring</SelectItem>
                    <SelectItem value="celebratory and cheerful" className="focus:bg-[#EDF1F8]">Celebratory & Cheerful</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              data-testid="generate-message-btn"
              className="w-full bg-gradient-to-r from-[#EDF1F8] to-indigo-600 hover:from-indigo-600 hover:to-[#E7EDF6] text-white"
            >
              {generating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>) : (<><Sparkles className="h-4 w-4 mr-2" /> {genMessage ? "Regenerate" : "Generate Message"}</>)}
            </Button>

            {genMessage && (
              <div className="space-y-4 border-t border-[#C9D4E6] pt-4">
                <div>
                  <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Template Title</label>
                  <input
                    type="text"
                    value={genTitle}
                    onChange={(e) => setGenTitle(e.target.value)}
                    data-testid="ai-title-input"
                    placeholder="Short name for this template"
                    className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-lg text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Short Description (optional)</label>
                  <input
                    type="text"
                    value={genDescription}
                    onChange={(e) => setGenDescription(e.target.value)}
                    data-testid="ai-description-input"
                    placeholder="When to use this message"
                    className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-lg text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Message (edit as needed)</label>
                  <Textarea
                    value={genMessage}
                    onChange={(e) => setGenMessage(e.target.value)}
                    data-testid="ai-message-output"
                    className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A] min-h-[220px] font-mono text-sm whitespace-pre-wrap"
                  />
                  <p className="text-xs text-[#3A4557] mt-1.5">Tip: keep <span className="text-[#A87608]">(Client Name)</span> to personalise per client.</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowGenModal(false)} className="border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8]">Cancel</Button>
            <Button
              onClick={handleSaveNew}
              disabled={!genMessage || saving}
              data-testid="save-template-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>) : (<><Check className="h-4 w-4 mr-2" /> Save as Template</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Modal */}
      <Dialog open={!!editTemplate} onOpenChange={(o) => { if (!o) setEditTemplate(null); }}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1B2A4A]">
              <Pencil className="h-5 w-5 text-[#A87608]" />
              Edit Template
            </DialogTitle>
          </DialogHeader>
          {editTemplate && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Title</label>
                <input
                  type="text"
                  value={editTemplate.title}
                  onChange={(e) => setEditTemplate({ ...editTemplate, title: e.target.value })}
                  data-testid="edit-title-input"
                  className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-lg text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Category</label>
                  <Select value={editTemplate.category} onValueChange={(v) => setEditTemplate({ ...editTemplate, category: v })}>
                    <SelectTrigger className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A]" data-testid="edit-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#C9D4E6] text-[#1B2A4A]">
                      {MESSAGE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id} className="focus:bg-[#EDF1F8]">{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Description</label>
                  <input
                    type="text"
                    value={editTemplate.description || ""}
                    onChange={(e) => setEditTemplate({ ...editTemplate, description: e.target.value })}
                    data-testid="edit-description-input"
                    className="w-full px-3 py-2.5 bg-[#F4F6FA] border border-[#C9D4E6] rounded-lg text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2E3B57] mb-1.5 block">Message</label>
                <Textarea
                  value={editTemplate.message}
                  onChange={(e) => setEditTemplate({ ...editTemplate, message: e.target.value })}
                  data-testid="edit-message-input"
                  className="bg-[#F4F6FA] border-[#C9D4E6] text-[#1B2A4A] min-h-[220px] font-mono text-sm whitespace-pre-wrap"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditTemplate(null)} className="border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8]">Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="save-edit-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>) : (<><Check className="h-4 w-4 mr-2" /> Save Changes</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="bg-white border-[#C9D4E6] text-[#1B2A4A] max-w-md" data-testid="delete-message-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1B2A4A]">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Template
            </DialogTitle>
            <DialogDescription className="text-[#3A4557]">
              Are you sure you want to delete <span className="text-[#1B2A4A] font-medium">"{deleteTarget?.title}"</span>? This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8]">Cancel</Button>
            <Button onClick={handleDelete} disabled={saving} data-testid="confirm-delete-btn" className="bg-red-600 hover:bg-red-700 text-white">
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Deleting...</>) : (<><Trash2 className="h-4 w-4 mr-2" /> Delete</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
